/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React from 'react';

/**
 * Where to send someone to install the AthloHive mobile app. Lives beside the
 * icon because the two are always used together; any later "get the app"
 * prompt should import from here rather than writing the URL out again.
 *
 * The package id is the Android build's `applicationId`
 * (upvibe/app/build.gradle.kts), NOT the `com.upvibe.app` Java namespace that
 * still appears in some older strings.
 */

/** Android `applicationId` of the published AthloHive app. */
export const ANDROID_PACKAGE = 'com.rudracoresoft.athlohive';

/** The app's Play Store listing. */
export const PLAY_STORE_URL =
  `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;

/**
 * PlayStoreIcon Component
 *
 * Google Play's four-color mark, drawn inline for the same reason as
 * GoogleIcon: a font glyph (bi-google-play) can only ever be one flat color.
 *
 * Two things make a hand-made version read as "nearly right", and both are
 * handled here:
 *  - Google redrew this mark flat. The gradient-filled version everyone
 *    remembers is the pre-2022 logo; the current one is four solid Google
 *    brand colors with rounded corners.
 *  - It is not four separate triangles. It is one shape folded along the
 *    horizontal axis through its tip, so the faces share vertices and meet
 *    cleanly on that axis. The rounding comes from stroking each face in its
 *    own color with a round line join, which is what softens the three outer
 *    points without disturbing the fold.
 *
 * Props:
 * - size: (number) width/height in pixels, default 18.
 */
const FACES = [
  // [fill, path] — left fold, right tip, lower face, upper face.
  ['#4285F4', 'M60.5 24.6c-4.4 4.7-7 12-7 21.4v420c0 9.5 2.6 16.7 7 21.4l1.4 1.4 235.3-235.3v-5.6L61.9 23.2l-1.4 1.4z'],
  ['#FBBC04', 'M374.8 334.3l-78.6-78.5v-5.6l78.6-78.6 1.8 1 93 52.8c26.6 15.1 26.6 39.8 0 54.9l-93 52.9-1.8 1.1z'],
  ['#EA4335', 'M376.6 333.2l-80.4-80.3L60.5 488.8c8.8 9.3 23.2 10.5 39.6 1.2l276.5-156.8'],
  ['#34A853', 'M376.6 172.6L100.1 15.8C83.7 6.5 69.3 7.7 60.5 17l235.7 235.9 80.4-80.3z'],
];

const PlayStoreIcon = ({ size = 18, style = {} }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 512 512"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    style={{ flexShrink: 0, ...style }}
  >
    {FACES.map(([color, d]) => (
      <path
        key={color}
        d={d}
        fill={color}
        stroke={color}
        strokeWidth="26"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    ))}
  </svg>
);

export default PlayStoreIcon;
